import { GlobalStyles } from "@mui/material";
import { styled, ThemeProvider } from "@mui/material/styles";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  lazy,
  MutableRefObject,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  HashRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { ConfirmProvider } from "material-ui-confirm";
import { IntlProvider } from "react-intl";

import { Box, createTheme, CssBaseline } from "@mui/material";

import { useTranslation } from "common/locales";
import { useIsMobile, useUserType } from "common/utils";
import { useUserFarms } from "model";

import { BottomTabNavigation, PushNotificationModal } from "common/components";
import DevUserSwitcher from "common/components/DevUserSwitcher";
import localeData from "common/locales";

import { queryClient } from "common/queryClient";
import { useTokenRefresh } from "common/utils";
import NotificationProvider from "./NotificationProvider";
import RefreshProvider from "./RefreshProvider";

import { PrivateRoute } from "app";
import LoadingScreen, {
  SuspenseFallback,
} from "common/components/LoadingScreen";
import { NewSignupDialog } from "features/signup";
import { MapRef } from "react-map-gl/mapbox";
import LoginDialog from "./LoginDialog";

import { App as CapacitorApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import ForceUpgradeScreen from "common/components/ForceUpgradeScreen";
import UpgradeNudgeDialog from "common/components/UpgradeNudgeDialog";
import { useForceUpgrade } from "common/utils/useForceUpgrade";
import FarmBottomSheetRouter from "features/farmOverview/FarmBottomSheetRouter";
// Desktop panel components - still statically imported for special handling (insight panels with dropdowns)
import { AccessibleFarmsInsights } from "features/insights/AccessibleFarmsInsights";
import { FarmInsights } from "features/insights/FarmInsights";
import { ProjectInsightsMetrics } from "features/insights/ProjectInsightsMetrics";
import { OnboardingChecklist, OnboardingFab } from "features/onboarding";
import InviteLandingPage from "features/programmeInvitations/InviteLandingPage";
import ProgrammesAction from "features/programmesActions/ProgrammesActions";
import Oauth2Screen from "features/signup/Oauth2RedirectScreen";
import SignupIntroDialog from "features/signup/SignupIntroDialog";
import { UpdateBiomassAfterMoveInDialog } from "features/singleCoverInput";
import { drawerWidth } from "width";
import PasswordForgotDialog from "./PasswordForgotDialog";
import PasswordResetDialog from "./PasswordResetDialog";

// Layout imports
import { useAppStore, useUIStore } from "stores";
import { AppLayout } from "./layouts";
import { DesktopRouteRenderer, MobileRouteRenderer } from "./RouteRenderer";
import { getDefaultRoute } from "./routes";
// Import theme constants from separate file to enable Fast Refresh
import { BORDER_RADIUS, COLORS, SPACING_UNIT } from "./theme";

// Lazy-loaded component for programme details (used in special handling)
const ProgrammeDetailsWeb = lazy(
  () => import("features/programmesActions/ProgrammeDetailsWeb")
);

declare module "@mui/material/styles" {
  // Brand color palette extensions
  interface Palette {
    dahlia: {
      900: string;
      700: string;
      500: string;
      300: string;
      100: string;
      50: string;
    };
    bellflower: {
      900: string;
      700: string;
      500: string;
      300: string;
      100: string;
      50: string;
    };
    daisy: {
      900: string;
      700: string;
      500: string;
      300: string;
      100: string;
      50: string;
    };
    // Legacy colors (to be phased out)
    carbon: PaletteColor;
    drawTool: PaletteColor;
  }
  interface PaletteOptions {
    dahlia?: {
      900?: string;
      700?: string;
      500?: string;
      300?: string;
      100?: string;
      50?: string;
    };
    bellflower?: {
      900?: string;
      700?: string;
      500?: string;
      300?: string;
      100?: string;
      50?: string;
    };
    daisy?: {
      900?: string;
      700?: string;
      500?: string;
      300?: string;
      100?: string;
      50?: string;
    };
    // Legacy colors (to be phased out)
    carbon?: PaletteColorOptions;
    drawTool?: PaletteColorOptions;
  }
}

const { palette } = createTheme();
const { augmentColor } = palette;
const createColor = (mainColor: string) =>
  augmentColor({ color: { main: mainColor } });

// Helper for component styleOverrides (used before theme is created)
const spacing = (value: number) => `${SPACING_UNIT * value}px`;

const theme = createTheme({
  palette: {
    // Primary: Dahlia green (main brand color)
    primary: {
      main: COLORS.dahlia[900],
      light: COLORS.dahlia[500],
      dark: COLORS.dahlia[900],
      contrastText: "#FFFFFF",
    },
    // Secondary: Bellflower purple
    secondary: {
      main: COLORS.bellflower[700],
      light: COLORS.bellflower[300],
      dark: COLORS.bellflower[900],
      contrastText: "#FFFFFF",
    },
    // Warning: Daisy yellow/gold
    warning: {
      main: COLORS.daisy[500],
      light: COLORS.daisy[300],
      dark: COLORS.daisy[700],
      contrastText: COLORS.daisy[900],
    },
    // Success: Dahlia green (lighter shade)
    success: {
      main: COLORS.dahlia[700],
      light: COLORS.dahlia[300],
      dark: COLORS.dahlia[900],
      contrastText: "#FFFFFF",
    },
    // Info: Bellflower purple
    info: {
      main: COLORS.bellflower[500],
      light: COLORS.bellflower[100],
      dark: COLORS.bellflower[700],
      contrastText: COLORS.bellflower[900],
    },
    // Error: Keep MUI default red for critical errors
    error: {
      main: "#D32F2F",
      light: "#EF5350",
      dark: "#C62828",
    },
    // Background colors
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
    },
    // Text colors
    text: {
      primary: COLORS.dahlia[900],
      secondary: "#6B7280", // Neutral gray for labels/secondary text
    },
    // Brand color palettes (full access)
    dahlia: COLORS.dahlia,
    bellflower: COLORS.bellflower,
    daisy: COLORS.daisy,
    // Legacy colors (to be phased out - keep for backwards compatibility)
    carbon: createColor("#C89F3E"),
    drawTool: createColor("#FF0000"),
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem",
      fontWeight: 700, // Inter Bold - for headlines
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 700, // Inter Bold - for headlines
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    h4: {
      fontSize: "1rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    h5: {
      fontSize: "0.875rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    h6: {
      fontSize: "0.75rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600, // Inter Semi Bold - for subtitles
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400, // Inter Regular - for body text
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400, // Inter Regular - for body text
    },
    button: {
      fontSize: "0.875rem",
      fontWeight: 500, // Inter Medium
      textTransform: "none",
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 400, // Inter Regular
    },
    overline: {
      fontSize: "0.625rem",
      fontWeight: 500, // Inter Medium - for uppercase
      textTransform: "uppercase",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    // Buttons - small radius (8px)
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS.small,
        },
      },
    },
    // Cards - medium radius (16px) for larger elements
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS.medium,
        },
      },
    },
    // Paper - small radius (8px) as default
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: BORDER_RADIUS.small,
        },
      },
    },
    // Dialogs - medium radius (16px) for larger elements
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: BORDER_RADIUS.medium,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: spacing(2),
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: spacing(2),
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: spacing(2),
        },
      },
    },
    // Chips - small radius (8px)
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS.small,
        },
      },
    },
    // Text fields - small radius (8px)
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS.small,
        },
      },
    },
    // Alerts - small radius (8px)
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: BORDER_RADIUS.small,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 0,
          marginRight: spacing(2),
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  // 8-point grid spacing: theme.spacing(1) = 8px
  spacing: SPACING_UNIT,
  // Corner radius: small=8px (default), medium=16px, large=24px
  shape: {
    borderRadius: BORDER_RADIUS.small, // Default to small (8px)
  },
});

// ============ DESKTOP-ONLY PANEL COMPONENTS ============
// These panels are only rendered on desktop and contain specialized desktop UI

function FarmInsightsContentPanel() {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  if (isMobile) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        zIndex: 1300,
        backgroundColor: "transparent",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <FarmInsights />
      </Box>
    </Box>
  );
}

function AccessibleFarmsInsightsContentPanel() {
  const isMobile = useIsMobile();

  if (isMobile) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <AccessibleFarmsInsights />
      </Box>
    </Box>
  );
}

function ProjectInsightsContentPanel() {
  const isMobile = useIsMobile();

  if (isMobile) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <ProjectInsightsMetrics />
      </Box>
    </Box>
  );
}

function ProgrammeDetailsRoute() {
  const isMobile = useIsMobile();

  // Cleanup questionnaireStarted state when navigating away from programme details
  useEffect(() => {
    return () => {
      useUIStore.setState({ questionnaireStarted: false });
    };
  }, []);

  if (isMobile) return null;

  return (
    <>
      <ProgrammeDetailsContentPanel />
      <ProgrammeDetailsQuestionnairePanel />
    </>
  );
}

function ProgrammeDetailsContentPanel() {
  const questionnaireStarted = useUIStore(
    (state) => state.questionnaireStarted
  );

  if (questionnaireStarted) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <ProgrammeDetailsWeb />
    </Box>
  );
}

function ProgrammeDetailsQuestionnairePanel() {
  const questionnaireStarted = useUIStore(
    (state) => state.questionnaireStarted
  );

  if (!questionnaireStarted) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "transparent",
      }}
    >
      <ProgrammesAction />
    </Box>
  );
}

// ============ MOBILE CONTENT COMPONENT ============
// Renders full-screen content for mobile routes
// Farm-tab routes are rendered inside FarmBottomSheetRouter (bottom sheet with routing)

function MobileContent({
  mapRef,
}: {
  mapRef: MutableRefObject<MapRef | null>;
}) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      <UpdateBiomassAfterMoveInDialog />

      {/* Farm-tab routes - rendered inside the bottom sheet */}
      <FarmBottomSheetRouter mapRef={mapRef} />

      {/* Non-farm-tab routes - rendered by RouteRenderer */}
      <Suspense fallback={<SuspenseFallback />}>
        <MobileRouteRenderer mapRef={mapRef} />
      </Suspense>
    </Box>
  );
}

// ============ FARM REDIRECT COMPONENT ============
// Redirects /farm (without ID) to appropriate route based on farms availability

function FarmRedirect() {
  const navigate = useNavigate();
  // Use proper React Query hook for farms
  const { data: allFarms, isLoading: farmsLoading } = useUserFarms();
  // Use Zustand for current farm ID
  const currentFarmId = useAppStore((state) => state.currentFarmId);

  useEffect(() => {
    // If we have a current farm, redirect to it
    if (currentFarmId) {
      navigate(`/farm/${currentFarmId}`, { replace: true });
      return;
    }

    // If still loading, wait
    if (farmsLoading) {
      return;
    }

    // If user has farms but no current one selected, redirect to first farm
    if (allFarms.length > 0) {
      navigate(`/farm/${allFarms[0].guid}`, { replace: true });
      return;
    }

    // No farms - redirect to add-farm flow
    navigate("/add-farm/enter-name", { replace: true });
  }, [navigate, allFarms, farmsLoading, currentFarmId]);

  return <LoadingScreen />;
}

// ============ HOME REDIRECT COMPONENT ============
// Redirects root path (/) to appropriate route based on user type

function HomeRedirect() {
  const navigate = useNavigate();
  const userType = useUserType();
  // Use Zustand for current farm ID
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  // Use proper React Query hook for farms
  const { data: allFarms } = useUserFarms();

  useEffect(() => {
    const farmId = currentFarmId || allFarms[0]?.guid;
    const defaultRoute = getDefaultRoute(
      userType,
      farmId as string | undefined
    );
    navigate(defaultRoute, { replace: true });
  }, [navigate, userType, currentFarmId, allFarms]);

  return <LoadingScreen />;
}

// ============ DESKTOP CONTENT COMPONENT ============
// Renders desktop panel content (positioned to right of drawer)
// Standard routes are rendered by DesktopRouteRenderer
// Special routes with custom UI (insights panels, programme details) are handled inline

function DesktopContent({
  mapRef,
}: {
  mapRef: MutableRefObject<MapRef | null>;
}) {
  const userType = useUserType();
  const isCorporateUser = userType === "corporate";

  return (
    <>
      {/* Standard routes - rendered by RouteRenderer with lazy-loaded components */}
      <DesktopRouteRenderer mapRef={mapRef} />

      {/* Special desktop handling routes - have custom UI with dropdowns, conditional rendering, etc. */}
      <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          <Route
            path="/farm-insights"
            element={
              <PrivateRoute>
                <FarmInsightsContentPanel />
              </PrivateRoute>
            }
          />
          {isCorporateUser && (
            <Route
              path="/programmes"
              element={
                <PrivateRoute>
                  <ProjectInsightsContentPanel />
                </PrivateRoute>
              }
            />
          )}
          <Route
            path="/farmers"
            element={
              <PrivateRoute>
                <AccessibleFarmsInsightsContentPanel />
              </PrivateRoute>
            }
          />
          <Route
            path="/programme-details/:programmeId"
            element={
              <PrivateRoute>
                <ProgrammeDetailsRoute />
              </PrivateRoute>
            }
          />

          {/* /farm redirect - not in routes.ts, handled specially */}
          <Route
            path="/farm"
            element={
              <PrivateRoute>
                <FarmRedirect />
              </PrivateRoute>
            }
          />

          {/* Catch-all: render nothing for paths handled by other Routes components */}
          <Route path="*" element={null} />
        </Routes>
      </Suspense>
    </>
  );
}

// ============ MAIN APP CONTENT ============

function AppContent() {
  const mapRef = useRef<MapRef>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Use Zustand for auth and current farm ID
  const isLoggedIn = useAppStore((state) => state.isAuthenticated);
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const loading = useAppStore((state) => state.loading);
  const isMobile = useIsMobile();
  const userType = useUserType();

  // App is ready when authenticated and not loading
  const isAppReady = isLoggedIn && loading === "idle";

  // Use Zustand for push notification modal (already migrated)
  const pushNotificationModal = useUIStore(
    (state) => state.pushNotificationModal
  );
  const closePushNotificationModal = useUIStore(
    (state) => state.closePushNotificationModal
  );

  // Hide splash screen after first paint is complete
  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void SplashScreen.hide();
      });
    });
  }, []);

  // Handle Android back button
  useEffect(() => {
    const handleBackButton = async () => {
      const activeElement = document.activeElement;
      const isMapEvent = activeElement?.closest(
        '[data-source="map-container"]'
      );

      if (isMapEvent) return;

      try {
        const defaultRoute = getDefaultRoute(userType, currentFarmId);
        if (
          !isLoggedIn &&
          (location.pathname === "/" || location.pathname === "/login")
        ) {
          await CapacitorApp.exitApp();
        } else if (isLoggedIn && location.pathname === defaultRoute) {
          await CapacitorApp.exitApp();
        } else {
          navigate(-1);
        }
      } catch (error) {
        console.error("Error handling back button:", error);
      }
    };

    const backButtonHandle = CapacitorApp.addListener(
      "backButton",
      handleBackButton
    );
    // Remove only this listener (not removeAllListeners) so it can't clobber
    // the deep-link listener registered below — this effect re-runs on every
    // navigation.
    return () => {
      void backButtonHandle.then((h) => h.remove());
    };
  }, [navigate, location, isLoggedIn, currentFarmId, userType]);

  // Deep links: route to in-app screens when the app is launched or resumed via
  // a universal/app link (e.g. the invite email's https://…/#/invite/<token>).
  // Capacitor loads the web bundle at its own base URL and drops the tapped
  // URL's path/fragment, so we read it back and navigate ourselves.
  useEffect(() => {
    const applyDeepLink = (url: string) => {
      try {
        const parsed = new URL(url);
        // Links use HashRouter paths (#/invite/…); fall back to a bare path for
        // links that omit the hash.
        const target = parsed.hash
          ? parsed.hash.replace(/^#/, "")
          : parsed.pathname;
        if (target && target !== "/") navigate(target);
      } catch {
        // ignore malformed deep-link URLs
      }
    };
    // Cold start: app was launched by tapping the link.
    void CapacitorApp.getLaunchUrl().then((res) => {
      if (res?.url) applyDeepLink(res.url);
    });
    // Warm start: app was already running when the link was tapped.
    const handle = CapacitorApp.addListener("appUrlOpen", ({ url }) =>
      applyDeepLink(url)
    );
    return () => {
      void handle.then((h) => h.remove());
    };
  }, [navigate]);

  return (
    <Root data-app-ready={isAppReady}>
      {/* Public routes (login, signup, etc.) */}
      <Routes>
        <Route path="/login" element={<LoginDialog />} />
        <Route path="/signup/*" element={<NewSignupDialog />} />
        <Route path="/signup-intro" element={<SignupIntroDialog />} />
        <Route
          path="/oauth2"
          element={
            <PrivateRoute>
              <Oauth2Screen />
            </PrivateRoute>
          }
        />
        <Route
          path="/login/password-forgot"
          element={<PasswordForgotDialog />}
        />
        <Route path="/login/password-reset" element={<PasswordResetDialog />} />
        {/* Invite landing must be a top-level route so it renders even with the
            AppLayout shell hidden for unauthenticated farmers. */}
        <Route path="/invite/:token" element={<InviteLandingPage />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <HomeRedirect />
            </PrivateRoute>
          }
        />

        {/* Catch-all: render nothing for paths handled by other Routes components */}
        <Route path="*" element={null} />
      </Routes>

      {/* App layout with mobile/desktop content */}
      <AppLayout mapRef={mapRef}>
        {isMobile ? (
          <MobileContent mapRef={mapRef} />
        ) : (
          <DesktopContent mapRef={mapRef} />
        )}
      </AppLayout>

      {/* Bottom Tab Navigation for Mobile */}
      <BottomTabNavigation />

      {/* Global Push Notification Modal */}
      <PushNotificationModal
        open={pushNotificationModal.open}
        onClose={closePushNotificationModal}
        notificationData={pushNotificationModal.data}
      />

      {/* Dev-only user switcher (only renders in development mode) */}
      <DevUserSwitcher />

      {/* Onboarding checklist for farmers only - corporate users should never see this */}
      {isLoggedIn && userType === "farmer" && (
        <>
          <OnboardingChecklist />
          <OnboardingFab />
        </>
      )}
    </Root>
  );
}

function ForceUpgradeGate({ children }: { children: React.ReactNode }) {
  const { checking, blocked, nudge, storeUrl, isWeb, dismissNudge } =
    useForceUpgrade();

  if (checking) return null;

  const handleUpdate = () => {
    if (isWeb) {
      window.location.reload();
    } else {
      window.open(storeUrl, "_blank");
    }
  };

  if (blocked) {
    void SplashScreen.hide();
    return (
      <ForceUpgradeScreen
        storeUrl={storeUrl}
        onUpdate={isWeb ? handleUpdate : undefined}
      />
    );
  }

  return (
    <>
      {children}
      <UpgradeNudgeDialog
        open={nudge}
        onUpdate={handleUpdate}
        onDismiss={dismissNudge}
      />
    </>
  );
}

export default function App() {
  // Use Zustand for locale
  const locale = useAppStore((state) => state.locale);
  const [messages, setMessages] = useState(localeData["en"]);
  useTokenRefresh();

  // Update messages when locale changes
  useEffect(() => {
    if (Object.keys(localeData).includes(locale)) {
      setMessages(localeData[locale]);
    } else {
      setMessages(localeData["en"]);
    }
  }, [locale]);

  return (
    <QueryClientProvider client={queryClient}>
      <IntlProvider locale={locale} key={locale} messages={messages}>
        <HashRouter>
          <GlobalStyles styles={{ "*": { touchAction: "pan-x pan-y" } }} />
          <CssBaseline />
          <ThemeProvider theme={theme}>
            <NotificationProvider>
              <ConfirmProvider>
                <RefreshProvider>
                  <ForceUpgradeGate>
                    <AppContent />
                  </ForceUpgradeGate>
                </RefreshProvider>
              </ConfirmProvider>
            </NotificationProvider>
          </ThemeProvider>
        </HashRouter>
      </IntlProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-right" />
    </QueryClientProvider>
  );
}

const Root = styled("div")({
  flexGrow: 1,
});
